import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Label from '../components/texts/label/Label';
import Button from '../components/button/Button';
import DropDown from '../components/dropdown/DropDown';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import TruncatedTextWithTooltip from '../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import Image from '../components/image/Image';
import Badge from '../components/badge/Badge';
import Icon from '../components/icon/Icon';
import {
  getProcessingLogFilters,
  searchProcessingLog,
  exportProcessingLog,
  type ProcessingLogFilters,
  type ProcessingLogItem,
  type ProcessingLogSearchResult,
} from '../services/GuaranteesService';
import { getSgrData } from '../services/SgrService';
import { routes } from '../routes/routePaths';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';

const TYPE_LABELS: Record<string, string> = {
  ECHEQ: 'Cheques E-Pyme',
  PAGARE: 'Pagarés E-Pyme',
  MAV_CHEQUE: 'MAV — Cheques',
  MAV_PAGARE: 'MAV — Pagarés',
};

const TYPE_KEBAB: Record<string, string> = {
  ECHEQ: 'cheques-epyme',
  PAGARE: 'pagares-epyme',
  MAV_CHEQUE: 'mav-cheques',
  MAV_PAGARE: 'mav-pagares',
};

const YELLOW_MOTIVOS = new Set(['CHEQUE_DUPLICADO', 'DUPLICADO']);

const MOTIVO_LABELS: Record<string, string> = {
  ERROR_TECNICO: 'Error técnico',
  SALDO_INSUFICIENTE: 'Saldo insuficiente',
  AVAL_MAXIMO_CLIENTE_SUPERADO: 'Aval máximo cliente superado',
  CHEQUE_DUPLICADO: 'Cheque duplicado',
  DUPLICADO: 'Duplicado',
  NO_ENCONTRADO: 'No encontrado',
  PRODUCTO_NO_HABILITADO: 'Producto no habilitado',
  LINEA_NO_APROBADA: 'Línea no aprobada',
  MONTO_EXCEDE_DISPONIBLE: 'Monto excede disponible',
};

const formatMotivo = (motivo: string): string =>
  MOTIVO_LABELS[motivo] ?? motivo.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());

const formatMoney = (value: number | null): string => {
  if (value == null) return '-';
  return `$ ${value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ProcessingHistory = () => {
  const fiscalId = useFiscalIdConfig();
  const navigate = useNavigate();

  const [sgrCuit, setSgrCuit] = useState<string>('');
  const [filters, setFilters] = useState<ProcessingLogFilters>({ dates: [], types: [] });
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResult, setSearchResult] = useState<ProcessingLogSearchResult | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const sgrResponse = await getSgrData();
        if (sgrResponse.success && sgrResponse.data?.cuit) {
          const cuit = sgrResponse.data.cuit;
          setSgrCuit(cuit);
          const filtersData = await getProcessingLogFilters(cuit);
          setFilters(filtersData);
          if (filtersData.dates.length === 0 && filtersData.types.length === 0) {
            setNoData(true);
          }
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      } finally {
        setIsLoadingFilters(false);
      }
    };
    loadFilters();
  }, []);

  const handleSearch = useCallback(async (page: number = 1) => {
    if (!selectedDate || !selectedType || !sgrCuit) return;
    setIsLoading(true);
    setHasSearched(true);
    try {
      const result = await searchProcessingLog(sgrCuit, selectedDate, selectedType, page, 50);
      setSearchResult(result);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching:', error);
      setSearchResult(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, selectedType, sgrCuit]);

  const handleExport = async () => {
    if (!selectedDate || !selectedType || !sgrCuit) return;
    setIsExporting(true);
    try {
      const response = await exportProcessingLog(sgrCuit, selectedDate, selectedType);
      if (response && response.data) {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const typeKebab = TYPE_KEBAB[selectedType] || selectedType.toLowerCase().replace(/_/g, '-');
        a.download = `historial-procesos-${typeKebab}-${selectedDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleChangePage = (page: number) => {
    handleSearch(page + 1);
  };

  const handleDateChange = (label: string) => {
    setSelectedDate(label);
    setHasSearched(false);
    setSearchResult(null);
  };

  const handleTypeChange = (label: string) => {
    const typeEntry = Object.entries(TYPE_LABELS).find(([, v]) => v === label);
    setSelectedType(typeEntry ? typeEntry[0] : '');
    setHasSearched(false);
    setSearchResult(null);
  };

  const canSearch = selectedDate && selectedType;
  const canExport = searchResult && searchResult.totalElements > 0;

  const columns = [
    {
      name: fiscalId.label,
      selector: (row: ProcessingLogItem) => row.cuit,
      width: '130px',
    },
    {
      name: 'Razón social',
      selector: (row: ProcessingLogItem) => row.razonSocial,
      grow: 2,
    },
    {
      name: 'Monto',
      selector: (row: ProcessingLogItem) => row.monto,
      cell: (row: ProcessingLogItem) => (
        <P type='3' variant='neutral' modifier='regular' withMargin={false}>
          {formatMoney(row.monto)}
        </P>
      ),
      width: '160px',
    },
    {
      name: 'Motivo',
      selector: (row: ProcessingLogItem) => row.motivo,
      cell: (row: ProcessingLogItem) => {
        const variant = YELLOW_MOTIVOS.has(row.motivo) ? 'warning' : 'danger';
        return (
          <Badge type='text' variant={variant} size='sm'>
            {formatMotivo(row.motivo)}
          </Badge>
        );
      },
      width: '220px',
    },
    {
      name: 'Detalle',
      selector: (row: ProcessingLogItem) => row.detalle || '',
      cell: (row: ProcessingLogItem) => {
        const detalle = row.detalle || '';
        return (
          <TruncatedTextWithTooltip text={detalle} style={{ maxWidth: '250px' }}>
            <P type='3' variant='neutral' modifier='regular' withMargin={false}>
              {detalle}
            </P>
          </TruncatedTextWithTooltip>
        );
      },
      grow: 2,
    },
  ];

  const tableData = searchResult
    ? {
        page: currentPage - 1,
        size: 50,
        totalElements: searchResult.totalElements,
      }
    : { page: 0, size: 50, totalElements: 0 };

  return (
    <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
      <Card display='flex' alignItems='center' gap='16px'>
        <Button
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={() => navigate(routes.monetization)}
          aria-label="Volver a Monetización"
        />
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Historial de procesos masivos
        </H>
      </Card>

      {isLoadingFilters ? (
        <Card spacing='4'>
          <P type='2' variant='neutral'>Cargando filtros...</P>
        </Card>
      ) : noData ? (
        <Card spacing='4' display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
          <Image name='folder_empty' />
          <P type='1' variant='neutral' modifier='strong'>
            No hay procesamientos previos registrados
          </P>
        </Card>
      ) : (
        <>
          <Card display='flex' gap='16px' alignItems='flex-end' spacing='2' flexWrap='wrap'>
            <Card width='250px'>
              <DropDown
                id='filter-date'
                title={selectedDate || 'Fecha de proceso'}
                items={filters.dates.map((d) => ({ label: d }))}
                onChange={handleDateChange}
                size='md'
              />
            </Card>
            <Card width='250px'>
              <DropDown
                id='filter-type'
                title={selectedType ? TYPE_LABELS[selectedType] : 'Tipo de proceso'}
                items={filters.types.map((t) => ({ label: TYPE_LABELS[t] || t }))}
                onChange={handleTypeChange}
                size='md'
              />
            </Card>
            <Button
              variant='primary'
              disabled={!canSearch}
              onClick={() => handleSearch(1)}
              style={{ gap: '8px' }}
            >
              <Icon name='search_outline' variant='neutral' modifier='inverted' width={18} height={18} />
              Buscar
            </Button>
            <Button
              variant='secondary'
              disabled={!canExport || isExporting}
              onClick={handleExport}
            >
              {isExporting ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          </Card>

          {isLoading ? (
            <TableSkeleton />
          ) : hasSearched && searchResult ? (
            searchResult.totalElements === 0 ? (
              <Card spacing='4' display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
                <Image name='folder_empty' />
                <P type='1' variant='neutral' modifier='strong'>
                  No se encontraron items para los filtros seleccionados
                </P>
              </Card>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    alignItems: 'flex-start',
                    gap: '16px',
                    padding: '16px 0',
                    margin: '12px 0',
                  }}
                >
                  <div>
                    <Label type='3' variant='neutral' weight='bold' modifier='strong'>Resultados</Label>
                    <P type='1' weight='bold' variant='neutral' modifier='strong' withMargin={false}>
                      {searchResult.totalElements} items
                    </P>
                  </div>
                  <div>
                    <Label type='3' variant='neutral' weight='bold' modifier='strong'>Fecha</Label>
                    <P type='1' weight='bold' variant='neutral' modifier='strong' withMargin={false}>
                      {selectedDate}
                    </P>
                  </div>
                  <div>
                    <Label type='3' variant='neutral' weight='bold' modifier='strong'>Tipo</Label>
                    <P type='1' weight='bold' variant='neutral' modifier='strong' withMargin={false}>
                      {TYPE_LABELS[selectedType] || selectedType}
                    </P>
                  </div>
                  <div>
                    <Label type='3' variant='neutral' weight='bold' modifier='strong'>Total monto rechazado</Label>
                    <P type='1' weight='bold' variant='neutral' modifier='strong' withMargin={false}>
                      {formatMoney(searchResult.totalAmount)}
                    </P>
                  </div>
                </div>

                <Table
                  columns={columns}
                  data={searchResult.content}
                  tableData={tableData}
                  handleChangePage={handleChangePage}
                />
              </>
            )
          ) : null}
        </>
      )}
    </Card>
  );
};

export default ProcessingHistory;
